'use client';
import { useEffect, useState } from 'react';

import { CreateShiftData, Shift } from 'types/collections';

import { ShiftService, UpdateShiftData } from '@services/ShiftService';

/**
 * Hook for managing all shifts for a school
 */
export function useShifts() {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  const fetchShifts = async () => {
    setLoading(true);
    setError(null);
    setIsError(false);
    try {
      const data = await ShiftService.getShifts();
      setShifts(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch shifts';
      console.error('Failed to fetch shifts:', err);
      setError(errorMessage);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const createShift = async (shiftData: CreateShiftData) => {
    setCreating(true);
    setError(null);
    setIsError(false);
    try {
      const response = await ShiftService.createShift(shiftData);
      if (response.success && response.data) {
        setShifts((prev) => [...prev, response.data.shift]);
        return response.data.shift;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create shift';
      console.error('Failed to create shift:', err);
      setError(errorMessage);
      setIsError(true);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  return {
    shifts,
    loading,
    creating,
    error,
    isError,
    fetchShifts,
    createShift,
  };
}

/**
 * Hook for managing a single shift
 */
export function useShift(shiftId: string) {
  const [shift, setShift] = useState<Shift | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchShift = async () => {
    if (!shiftId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await ShiftService.getShift(shiftId);
      setShift(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch shift';
      console.error('Failed to fetch shift:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateShift = async (updateData: UpdateShiftData) => {
    if (!shiftId) return;

    setUpdating(true);
    setError(null);
    try {
      const response = await ShiftService.updateShift(shiftId, updateData);
      if (response.success && response.data) {
        setShift(response.data.shift);
        return response.data.shift;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update shift';
      console.error('Failed to update shift:', err);
      setError(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchShift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftId]);

  return {
    shift,
    loading,
    updating,
    error,
    fetchShift,
    updateShift,
  };
}
